import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Divider,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { USER_ROLES } from '../../constants/userRoles';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import { DataProviderInstance } from '../../api/DataProvider';
import { GetCompanyAutomationSettingsAPI, UpdateCompanyAutomationSettingAPI } from '../../api/API';
import { useWhatsAppSender } from '../../contexts/WhatsAppSenderContext';
import styles from './Preferences.module.css';
import CustomButton from '../../components/buttons/CustomButton';
import EnhancedDaySelector from '../../components/day-selector/EnhancedDaySelector';
import classNames from 'classnames';
import PublicScheduleSettings from '../company/areas/publicSchedule/PublicScheduleSettings';
import { AUTOMATION_GROUPS } from './automationsCatalog';
import AutomationItem from './components/AutomationItem';

const Preferences = () => {
    const { CompanyConfig, UpdateCompanyConfig } = DataProviderInstance();
    const { hasRole } = useRoleAccess();
    const [automationSettings, setAutomationSettings] = useState({});
    const [loading, setLoading] = useState(true);
    // trial_restricted / trial_allowed_types vêm do backend (AutomationSettings::isBlockedOnTrial).
    // Empresa em trial ativo (sem assinatura) só pode ligar as automações da lista do backend
    // (desde 2026-10-06, todas menos as que exigem número próprio).
    const [trialRestriction, setTrialRestriction] = useState({ restricted: false, allowedTypes: [] });
    // plan_restricted_types / plan_notes vêm do backend (AutomationSettings::getPlanRestrictedTypes /
    // getPlanNotes), independente do trial (plano-segregacao-por-plano.md, 7): automações de
    // relacionamento indisponíveis no plano atual, e nota informativa sobre reenvio de confirmação.
    const [planRestriction, setPlanRestriction] = useState({ restrictedTypes: {}, notes: {} });
    const { state: whatsappState } = useWhatsAppSender();
    // Só trava as automações pelo estado da conexão quando o número próprio está desconectado.
    // Sem número próprio (`source === 'blocked'`, já que desde 2026-10-23 nada ao paciente sai
    // pelo número da Gabriela) quem explica é plan_restricted_types, com a mensagem certa para
    // cada caso. Enquanto o status carrega, `state` vem null (fail-open do contexto).
    const whatsappConfigured = whatsappState !== 'own_unavailable';
    const whatsappTooltipMessage = 'Seu número de WhatsApp está desconectado. Reconecte em Configurações > WhatsApp';
    const trialTooltipMessage = 'Disponível ao assinar um plano. Durante o período de teste, esta automação não é enviada.';

    // Verificar se o usuário tem permissão para editar (COMPANY_OWNER ou COMPANY_ADMIN)
    const canEdit = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);

    // Converte timeInterval (fração de hora, ex: 0.5) para minutos inteiros (ex: 30)
    // para facilitar a exibição e edição no Select.
    // Valores válidos em minutos: 5, 10, 15, 20, 30, 45, 60
    const VALID_SLOT_MINUTES = [5, 10, 15, 20, 30, 45, 60];
    const DEFAULT_SLOT_MINUTES = 30;

    const timeIntervalToMinutes = (fraction) => {
        if (fraction == null) return DEFAULT_SLOT_MINUTES;
        const minutes = Math.round(fraction * 60);
        return VALID_SLOT_MINUTES.includes(minutes) ? minutes : DEFAULT_SLOT_MINUTES;
    };

    const minutesToTimeInterval = (minutes) => {
        return minutes / 60;
    };

    const initialState = {
        availableDaysOfWeek: CompanyConfig.AvailableDaysOfWeek,
        availableTime: CompanyConfig.AvailableTime,
        blockedTimes: CompanyConfig.BlockedTimes,
        targetOccupation: CompanyConfig.TargetOccupation,
        // slotMinutes: armazenado em minutos inteiros (ex: 30) para evitar problemas de comparação float.
        // Na hora de salvar, é convertido de volta para fração de hora (ex: 0.5) via minutesToTimeInterval().
        slotMinutes: timeIntervalToMinutes(CompanyConfig.TimeInterval),
        goalAppointments: CompanyConfig.GoalAppointments,
        goalBilling: CompanyConfig.GoalBilling
    };

    // Load automation settings
    useEffect(() => {
        GetCompanyAutomationSettingsAPI(CompanyConfig.Id, (response) => {
            if (response.success && response.settings) {
                // Convert array to object with automation_type as key
                const settingsObj = response.settings.reduce((acc, setting) => ({
                    ...acc,
                    [setting.automation_type]: setting.is_enabled
                }), {});

                setAutomationSettings(settingsObj);
            }
            setTrialRestriction({
                restricted: Boolean(response.trial_restricted),
                allowedTypes: response.trial_allowed_types || []
            });
            setPlanRestriction({
                restrictedTypes: response.plan_restricted_types || {},
                notes: response.plan_notes || {}
            });
            setLoading(false);
        });
    }, [CompanyConfig.Id]);

    // Se a automação fica bloqueada porque a empresa está em trial ativo (sem assinatura).
    const isTrialBlocked = (type) => {
        return trialRestriction.restricted && !trialRestriction.allowedTypes.includes(type);
    };

    // Se a automação fica bloqueada porque não cabe no plano atual (independente do trial).
    const isPlanBlocked = (type) => {
        return Boolean(planRestriction.restrictedTypes[type]);
    };

    // Check if automation type is WhatsApp related.
    // Todas as automações do catálogo dependem do WhatsApp (o backend também
    // checa a configuração de WhatsApp para google_review).
    const isWhatsappAutomation = (type) => {
        const whatsappTypes = AUTOMATION_GROUPS.flatMap(group => group.items.map(item => item.type));
        return whatsappTypes.includes(type);
    };

    // Handle automation toggle
    const handleAutomationToggle = (type) => {
        // If it's a WhatsApp automation and WhatsApp is not configured, don't allow toggle
        if (isWhatsappAutomation(type) && !whatsappConfigured) {
            return;
        }

        const newValue = !automationSettings[type];

        // Não permite ligar uma automação bloqueada pelo trial (a API também recusa)
        if (newValue && isTrialBlocked(type)) {
            return;
        }

        // Não permite ligar uma automação indisponível no plano atual (a API também recusa)
        if (newValue && isPlanBlocked(type)) {
            return;
        }

        // Optimistic update
        setAutomationSettings(prev => ({
            ...prev,
            [type]: newValue
        }));

        UpdateCompanyAutomationSettingAPI(CompanyConfig.Id, type, newValue, (response) => {
            if (!response.success) {
                // Revert on failure
                setAutomationSettings(prev => ({
                    ...prev,
                    [type]: !newValue
                }));
            }
        });
    };

    const publicScheduleRef = useRef(null);

    const [values, setValues] = useState(initialState);
    const [isEditable, setIsEditable] = useState(false);
    const [timeSlots, setTimeSlots] = useState([]);

    const handleChange = (field, value) => {
        setValues((prevValues) => ({
            ...prevValues,
            [field]: value,
        }));
    };

    const generateTimeSlots = (startTime, endTime, interval) => {
        const slots = [];
        for (let time = startTime; time < endTime; time += interval) {
            if (interval < 1) {
                const hour = Math.floor(time);
                const minutes = (time % 1) * 60;
                slots.push({ hour: time, display: `${hour}:${minutes.toString().padStart(2, '0')}` });
            } else {
                slots.push({ hour: time, display: `${Math.floor(time)}:00` });
            }
        }
        return slots;
    };

    useEffect(() => {
        const [start, end] = values.availableTime;
        if (start < end) {
            // generateTimeSlots usa timeInterval como fração de hora
            setTimeSlots(generateTimeSlots(start, end, minutesToTimeInterval(values.slotMinutes)));
        }
    }, [values.availableTime, values.slotMinutes]);

    const handleBlockedTimesChange = (timeSlot) => {
        const hour = timeSlot.hour;
        if (values.blockedTimes.includes(hour)) {
            handleChange('blockedTimes', values.blockedTimes.filter(time => time != hour));
        } else {
            handleChange('blockedTimes', [...values.blockedTimes, hour]);
        }
    };

    const handleEdit = () => {
        setIsEditable(true);
    };

    const handleSave = () => {
        if (values.availableTime[0] >= values.availableTime[1]) {
            alert('Horário de início não pode ser maior ou igual ao horário de término.');
            return;
        }

        // Converte slotMinutes de volta para fração de hora antes de salvar
        const dataToSave = {
            ...values,
            timeInterval: minutesToTimeInterval(values.slotMinutes),
        };

        UpdateCompanyConfig(dataToSave, (result) => {});

        // Salvar também as configurações de agendamento público
        if (publicScheduleRef.current) {
            publicScheduleRef.current.save();
        }

        setIsEditable(false);
    };

    const handleReset = () => {
        setValues(initialState);
        setIsEditable(false);
    };

    const ActionButtons = () => (
        <div className={styles.actionButtons}>
            {isEditable ? (
                <>
                    <span>
                        <CustomButton
                            variant="contained"
                            label="Salvar"
                            style="secondary"
                            onClick={handleSave}
                            icon={<SaveIcon />}
                        />
                    </span>
                    <CustomButton
                        variant="outlined"
                        label="Cancelar"
                        style="primary"
                        onClick={handleReset}
                        icon={<CloseIcon />}
                    />
                </>
            ) : (
                <Tooltip
                    title={!canEdit ? "Apenas proprietários e administradores podem editar as preferências" : ""}
                    arrow
                >
                    <span>
                        <CustomButton
                            variant="contained"
                            label="Editar"
                            style="secondary"
                            onClick={handleEdit}
                            icon={<EditIcon />}
                            disabled={!canEdit}
                        />
                    </span>
                </Tooltip>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className='page-content' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress color="primary" />
            </div>
        );
    }

    return (
        <div className='page-content'>
            <div className={styles.mainContainer}>
                <div className={styles.titleContainer}>
                    <AreaTitle
                        Title="Preferências"
                        Icon={<SettingsOutlinedIcon />}
                        Description="Configure os parâmetros gerais da sua clínica"
                    />
                    <ActionButtons />
                </div>

                {/* Horário de Funcionamento Section */}
                <Paper className={styles.section} elevation={0}>
                    <AreaTitle
                        Title="Horário de Funcionamento"
                        Icon={<AccessTimeOutlinedIcon />}
                        Description="Configure os dias e horários em que sua clínica estará disponível para agendamentos."
                    />
                    <Typography variant="caption" component="p" className={styles.automationFootnote} sx={{ mb: 2 }}>
                        <InfoOutlinedIcon fontSize="small" className={styles.automationFootnoteIcon} />
                        <span>
                            Vale para todas as agendas. Quem atende em dias, horários ou limites diferentes ganha horário próprio em{' '}
                            <Link to="/company/agendas" className={styles.automationFootnoteLink}>Agendas</Link>, ao editar a agenda.
                        </span>
                    </Typography>
                    <Box mb={2}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={500}>
                            Dias de Funcionamento
                        </Typography>
                        <EnhancedDaySelector
                            value={values.availableDaysOfWeek}
                            onChange={(newValue) => handleChange('availableDaysOfWeek', newValue)}
                            disabled={!isEditable}
                        />
                    </Box>
                    <div className={styles.timeSettingsRow}>
                        <TextField
                            className={styles.timeField}
                            label="Horário de Início"
                            type="number"
                            value={values.availableTime[0]}
                            onChange={(e) => handleChange('availableTime', [Number(e.target.value), values.availableTime[1]])}
                            disabled={!isEditable}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="medium"
                        />
                        <TextField
                            className={styles.timeField}
                            label="Horário de Término"
                            type="number"
                            value={values.availableTime[1]}
                            onChange={(e) => handleChange('availableTime', [values.availableTime[0], Number(e.target.value)])}
                            disabled={!isEditable}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="medium"
                        />
                        <FormControl className={styles.timeField} variant="outlined" size="medium">
                            <InputLabel shrink>Duração dos slots do calendário</InputLabel>
                            <Select
                                value={values.slotMinutes}
                                onChange={(e) => handleChange('slotMinutes', Number(e.target.value))}
                                disabled={!isEditable}
                                label="Duração dos slots do calendário"
                            >
                                <MenuItem value={5}>5 minutos</MenuItem>
                                <MenuItem value={10}>10 minutos</MenuItem>
                                <MenuItem value={15}>15 minutos</MenuItem>
                                <MenuItem value={20}>20 minutos</MenuItem>
                                <MenuItem value={30}>30 minutos</MenuItem>
                                <MenuItem value={45}>45 minutos</MenuItem>
                                <MenuItem value={60}>1 hora</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                </Paper>

                {/* Horários Bloqueados Section */}
                <Paper className={styles.section} elevation={0}>
                    <AreaTitle
                        Title="Horários Bloqueados"
                        Icon={<BlockOutlinedIcon />}
                        Description="Selecione os horários que não estarão disponíveis para agendamento."
                    />
                    <Typography variant="caption" component="p" className={styles.automationFootnote} sx={{ mt: 0, mb: 2 }}>
                        <InfoOutlinedIcon fontSize="small" className={styles.automationFootnoteIcon} />
                        <span>Não valem para agendas com horário próprio: nelas, o intervalo do almoço é o espaço entre as faixas.</span>
                    </Typography>
                    <div className={styles.blockedTimesGrid}>
                        {timeSlots.map((timeSlot) => (
                            <div
                                key={timeSlot.hour}
                                className={classNames(styles.timeChip, {
                                    [styles.blocked]: values.blockedTimes.includes(timeSlot.hour)
                                })}
                                onClick={() => isEditable && handleBlockedTimesChange(timeSlot)}
                                style={{ cursor: isEditable ? 'pointer' : 'default' }}
                            >
                                {timeSlot.display}
                            </div>
                        ))}
                    </div>
                </Paper>

                {/* Agendamento Público Section */}
                <PublicScheduleSettings ref={publicScheduleRef} isEditable={isEditable} />

                {/* Metas e Limites Section */}
                <Paper className={styles.section} elevation={0}>
                    <AreaTitle
                        Title="Metas e Limites"
                        Icon={<AssessmentOutlinedIcon />}
                        Description="Configure suas metas de agendamentos e faturamento para acompanhar seu desempenho."
                    />
                    <Box mt={3}>
                        <div className={styles.metricsGrid}>
                            <TextField
                                label="Máximo de Agendamentos por Dia"
                                type="number"
                                value={values.goalAppointments}
                                onChange={(e) => handleChange('goalAppointments', e.target.value)}
                                disabled={!isEditable}
                                fullWidth
                                className={styles.metricField}
                                variant="outlined"
                                InputProps={{
                                    inputProps: { min: 0 }
                                }}
                            />
                            <TextField
                                label="Meta de Agendamentos por Mês"
                                type="number"
                                value={values.targetOccupation}
                                onChange={(e) => handleChange('targetOccupation', e.target.value)}
                                disabled={!isEditable}
                                fullWidth
                                className={styles.metricField}
                                variant="outlined"
                                InputProps={{
                                    inputProps: { min: 0 }
                                }}
                            />
                            <TextField
                                label="Meta de Faturamento Mensal (R$)"
                                type="number"
                                value={values.goalBilling}
                                onChange={(e) => handleChange('goalBilling', e.target.value)}
                                disabled={!isEditable}
                                fullWidth
                                className={styles.metricField}
                                variant="outlined"
                                InputProps={{
                                    inputProps: { min: 0 }
                                }}
                            />
                        </div>
                    </Box>
                </Paper>

                {/* Automação Section */}
                <Paper className={styles.section} elevation={0}>
                    <AreaTitle
                        Title="Automação"
                        Icon={<SmartToyOutlinedIcon />}
                        Description="Mensagens pelo WhatsApp que a Gabriela prepara para você; com o seu número conectado, saem sozinhas. Ligue ou desligue cada uma; abaixo você vê quando e para quem cada mensagem é enviada."
                    />
                    {!whatsappConfigured && (
                        <Typography variant="body2" sx={{ color: 'error.main', mt: 1, mb: 2, display: 'flex', alignItems: 'center' }}>
                            <WhatsAppIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Seu número de WhatsApp está desconectado. Reconecte-o na tela de WhatsApp para voltar a enviar estas automações.
                        </Typography>
                    )}
                    {trialRestriction.restricted && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, mb: 2, display: 'flex', alignItems: 'center' }}>
                            <InfoOutlinedIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Durante o período de teste, as mensagens pelo WhatsApp saem com o seu próprio número conectado; o envio automático da nota fiscal fica disponível ao assinar um plano.{' '}
                            <Link to="/subscription/plans" className={styles.automationFootnoteLink} style={{ marginLeft: 4 }}>Ver planos</Link>
                        </Typography>
                    )}
                    <Divider sx={{ mb: 2 }} />
                    {AUTOMATION_GROUPS.map((group, groupIndex) => (
                        <div key={group.id} className={styles.automationGroup} style={groupIndex > 0 ? { marginTop: 'var(--spacing-lg)' } : undefined}>
                            <Typography className={styles.automationGroupTitle}>
                                {group.title}
                            </Typography>
                            <div className={styles.automationGrid}>
                                {group.items.map((item) => {
                                    const whatsappBlocked = isWhatsappAutomation(item.type) && !whatsappConfigured;
                                    const trialBlocked = !whatsappBlocked && isTrialBlocked(item.type);
                                    const planBlocked = !whatsappBlocked && !trialBlocked && isPlanBlocked(item.type);
                                    const planTooltipMessage = planRestriction.restrictedTypes[item.type]?.message;
                                    const tooltip = whatsappBlocked
                                        ? whatsappTooltipMessage
                                        : trialBlocked
                                            ? trialTooltipMessage
                                            : planTooltipMessage;
                                    return (
                                        <AutomationItem
                                            key={item.type}
                                            item={item}
                                            checked={Boolean(automationSettings[item.type])}
                                            disabled={!isEditable || whatsappBlocked || trialBlocked || planBlocked}
                                            blocked={whatsappBlocked || trialBlocked || planBlocked}
                                            tooltip={tooltip}
                                            note={planRestriction.notes[item.type]}
                                            onToggle={() => handleAutomationToggle(item.type)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    <Typography variant="caption" className={styles.automationFootnote}>
                        <InfoOutlinedIcon fontSize="small" className={styles.automationFootnoteIcon} />
                        Os horários de envio são definidos pela plataforma (horário de Brasília) e, por enquanto, não podem ser alterados. O texto das mensagens pode ser personalizado na tela{' '}
                        <Link to="/company/templates" className={styles.automationFootnoteLink}>Modelos</Link>
                        , na aba "Mensagens".
                    </Typography>
                </Paper>
            </div>
        </div>
    );
};

export default Preferences;